import React, { useMemo } from 'react';

import { AGGREGATION_FIELDS } from '@/shared/api/openmetadata';
import type { OpenMetadataAggregations } from '@/shared/api/openmetadata';
import { highlightText } from '@/shared/lib/text';
import { FilterDropdown, sortFilterOptions } from '@/shared/ui/FilterDropdown';
import type { FilterDropdownOption } from '@/shared/ui/FilterDropdown';
import { Icon } from '@/shared/ui/Icon';

import { useFilterStore } from '@/entities/discovery';
import { useSearchStore } from '@/entities/search/domain/store';

import styles from './OwnerDropdown.module.css';

type OwnerType = 'team' | 'user';

const getOwnerType = (ownerName: string): OwnerType => {
  return ownerName.includes('.') ? 'user' : 'team';
};

const generateOwnerOptionsFromAggregations = (
  initialAggregations: OpenMetadataAggregations | null,
  selectedOwners: string[]
): FilterDropdownOption[] => {
  if (!initialAggregations) return [];

  const ownerBuckets = initialAggregations[AGGREGATION_FIELDS.OWNER]?.buckets || [];

  const options: FilterDropdownOption[] = ownerBuckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.key,
    value: bucket.key,
    disabled: false,
    data: {
      ownerType: getOwnerType(bucket.key),
      count: bucket.doc_count,
    },
  }));

  return sortFilterOptions(options, selectedOwners);
};

type OwnerDropdownProps = {
  width?: number | string;
};

export const OwnerDropdown: React.FC<OwnerDropdownProps> = ({ width }) => {
  const { filters, updateOwnerFilter } = useFilterStore();
  const { initialAggregations } = useSearchStore();

  const selectedOwners = useMemo(() => filters.owner || [], [filters.owner]);

  const ownerOptions = useMemo(() => {
    return generateOwnerOptionsFromAggregations(initialAggregations, selectedOwners);
  }, [initialAggregations, selectedOwners]);

  return (
    <FilterDropdown
      buttonText="소유자"
      searchPlaceholder="이름, 팀 검색..."
      selectedValues={selectedOwners}
      options={ownerOptions}
      onSelectionChange={updateOwnerFilter}
      width={width}
      getButtonTextForSingleSelection={(option) => option.label}
      renderItem={(option, _isSelected, _onToggle, searchQuery) => {
        const ownerType = (option.data?.ownerType as OwnerType) || 'user';
        return (
          <>
            <div className={styles.ownerIconWrapper}>
              <Icon name={ownerType === 'team' ? 'users' : 'user'} size={16} />
            </div>
            <div className={styles.ownerInfo}>
              {highlightText(option.label, searchQuery, { className: 'highlight' })}
            </div>
          </>
        );
      }}
    />
  );
};
